import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/components/live-match-bar.scss';
import { liveMatchService } from '../services/liveMatchService';
import { isBO3LiveMatch } from '../utils/matchUtils';
import axios from 'axios';
import { baseURL, powerStatsAPI } from '../Constants';

// Match data structure from /api/match/live endpoint
interface LiveMatchData {
  match_id: string;
  get5_match_id?: number;
  status: string;
  map_number: number;
  map_name: string;
  round_number: number;
  team1_id?: number;
  team1_name: string;
  team1_score: number;
  team1_series_score: number;
  team1_side?: string;
  team2_id?: number;
  team2_name: string;
  team2_score: number;
  team2_series_score: number;
  team2_side?: string;
  updated_at: string;
  hidden?: boolean;
}

// Team data from database
interface TeamData {
  teamId: string | number;
  teamName: string;
  teamLogo?: string;
}

// Enhanced match data with veto maps and team info
interface EnhancedLiveMatch extends LiveMatchData {
  vetoMaps?: string[];
  team1Data?: TeamData;
  team2Data?: TeamData;
}

interface LiveMatchBarProps {
  selectedSeason?: number;
  currentSeason?: number;
  maxDisplay?: number;
  isAdmin?: boolean;
}

const LiveMatchBar: React.FC<LiveMatchBarProps> = ({
  selectedSeason,
  currentSeason = 3, // Default to Season 3 as current season
  maxDisplay = 4,
  isAdmin = false
}) => {
  const [liveMatches, setLiveMatches] = useState<EnhancedLiveMatch[]>([]);

  // Only show live matches when viewing the current season
  const shouldShowLiveMatches = !selectedSeason || selectedSeason === currentSeason;

  // Parse veto log to get picked maps
  const parseVetoMaps = (vetoLog: string): string[] => {
    if (!vetoLog) return [];

    const lines = vetoLog.split('\n').filter(line => line.trim());
    const pickedMaps: string[] = [];

    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Remove timestamp if present
      let processedLine = trimmedLine;
      if (trimmedLine.includes(' - ')) {
        const dashIndex = trimmedLine.indexOf(' - ');
        const beforeDash = trimmedLine.substring(0, dashIndex);
        if (/^\d{1,2}:\d{2}:\d{1,3}$/.test(beforeDash.trim())) {
          processedLine = trimmedLine.substring(dashIndex + 3);
        }
      }

      // Check for picked maps
      if (processedLine.includes(' picked ')) {
        const parts = processedLine.split(' picked ');
        const map = parts[1];
        pickedMaps.push(map);
      }
      // Check for decider map - "X got MapName as the decider"
      else if (processedLine.includes(' got ') && processedLine.includes(' as')) {
        const match = processedLine.match(/got (.+?) as/);
        if (match) {
          pickedMaps.push(match[1].trim());
        }
      }
      // Check for "MapName was left over"
      else if (processedLine.includes(' was left over')) {
        const map = processedLine.split(' was left over')[0];
        if (map) pickedMaps.push(map.trim());
      }
    });

    console.log('[LiveMatchBar] Parsed veto maps:', pickedMaps);
    return pickedMaps;
  };

  // Fetch complete live match data from the server
  useEffect(() => {
    // Don't fetch if we're not showing live matches
    if (!shouldShowLiveMatches) {
      return;
    }

    const fetchLiveMatches = async () => {
      try {
        // Get all currently live matches with complete data
        const matches = await liveMatchService.getAllLiveMatches();

        // Fetch veto data for each match to get picked maps
        const enhancedMatches = await Promise.all(
          matches.map(async (match) => {
            try {
              // Fetch match details to get veto and team info
              const response = await axios.get(`${powerStatsAPI}/Match/getUpcomingMatchById/${match.match_id}`);
              const veto = response.data?.veto;
              const vetoMaps = veto ? parseVetoMaps(veto) : [];

              // Extract team data from match details
              const team1Data = response.data?.team1 ? {
                teamId: response.data.team1.teamId,
                teamName: response.data.team1.teamName,
                teamLogo: response.data.team1.teamLogo
              } : undefined;

              const team2Data = response.data?.team2 ? {
                teamId: response.data.team2.teamId,
                teamName: response.data.team2.teamName,
                teamLogo: response.data.team2.teamLogo
              } : undefined;

              console.log(`[LiveMatchBar] Match ${match.match_id} - Teams:`, {
                team1: team1Data?.teamName,
                team2: team2Data?.teamName,
                vetoMaps
              });

              return {
                ...match,
                vetoMaps,
                team1Data,
                team2Data,
                hidden: response.data?.hidden || false
              };
            } catch (error) {
              console.warn(`[LiveMatchBar] Could not fetch details for match ${match.match_id}`);
              return {
                ...match,
                vetoMaps: [],
                team1Data: undefined,
                team2Data: undefined,
                hidden: false
              };
            }
          })
        );

        setLiveMatches(enhancedMatches);
      } catch (error) {
        // Error handling is done in the service, just set empty array
        setLiveMatches([]);
      }
    };

    // Fetch immediately
    fetchLiveMatches();

    // Poll every 30 seconds to keep live matches updated
    const interval = setInterval(fetchLiveMatches, 30000);

    return () => clearInterval(interval);
  }, [shouldShowLiveMatches]);

  // Filter hidden matches for non-admins
  const visibleMatches = liveMatches.filter(match => isAdmin || !match.hidden);

  // Don't render if not current season or no live matches
  if (!shouldShowLiveMatches || visibleMatches.length === 0) {
    return null;
  }

  const displayedMatches = visibleMatches.slice(0, maxDisplay);
  const remainingCount = visibleMatches.length - displayedMatches.length;

  const getTeamLogoUrl = (teamData?: TeamData) => {
    if (!teamData) {
      // Inline SVG placeholder for teams without data (like TBD)
      return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
    }

    // Try direct logo URL first if it exists
    if (teamData.teamLogo && teamData.teamLogo.startsWith('http')) {
      return teamData.teamLogo;
    } else if (teamData.teamLogo) {
      return `${baseURL.replace('/api', '')}${teamData.teamLogo}`;
    }

    // Fallback to Team API endpoint
    if (teamData.teamId) {
      return `${baseURL}/Team/${teamData.teamId}/logo`;
    }

    // Final fallback to placeholder
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
  };

  return (
    <div className="live-match-bar">
      <div className="live-match-bar__header">
        <div className="live-indicator">
          <span className="live-dot"></span>
          <span className="live-text">LIVE</span>
        </div>
      </div>

      <div className="live-match-bar__matches">
        {displayedMatches.map(match => {
          // Use shared utility to determine if match is BO3
          const isBo3Match = isBO3LiveMatch(match);

          // Trust backend series scores directly
          const team1SeriesWins = match.team1_series_score;
          const team2SeriesWins = match.team2_series_score;

          return (
            <Link
              key={match.match_id}
              to={`/matches/${match.match_id}`}
              className="live-match-card"
            >
              <div className="live-match-card__header">
                <span className="match-type">
                  {isBo3Match ? 'BO3' : `BO1 • ${match.map_name}`}
                </span>
                <span className="match-status">
                  {match.status === 'pending_next_map' ? 'Between Maps' : 'LIVE'}
                </span>
              </div>

              <div className="live-match-card__teams">
                <div className="team">
                  <img
                    src={getTeamLogoUrl(match.team1Data)}
                    alt={match.team1_name}
                    className="team-logo"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (!target.src.includes('data:image')) {
                        target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
                      }
                    }}
                  />
                  <span className="team-name">{match.team1_name}</span>
                  <div className="team-score">
                    <span className="round-score">{match.team1_score}</span>
                    <span className="series-score">({team1SeriesWins})</span>
                  </div>
                </div>

                <div className="team">
                  <img
                    src={getTeamLogoUrl(match.team2Data)}
                    alt={match.team2_name}
                    className="team-logo"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (!target.src.includes('data:image')) {
                        target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
                      }
                    }}
                  />
                  <span className="team-name">{match.team2_name}</span>
                  <div className="team-score">
                    <span className="round-score">{match.team2_score}</span>
                    <span className="series-score">({team2SeriesWins})</span>
                  </div>
                </div>
              </div>

              <div className="live-match-card__footer">
                {isBo3Match ? (
                  <div className="map-progression">
                    {match.vetoMaps && match.vetoMaps.length > 0 ? (
                      // Show veto maps
                      match.vetoMaps.map((mapName, idx) => {
                        const cleanName = mapName.replace('de_', '');
                        const displayName = cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
                        const isCurrentMap = match.map_name === mapName || match.map_name === `de_${mapName.toLowerCase()}`;

                        return (
                          <React.Fragment key={idx}>
                            <span
                              className={`map-item ${isCurrentMap ? 'current' : ''}`}
                              title={displayName}
                            >
                              {displayName}
                            </span>
                            {idx < (match.vetoMaps?.length ?? 0) - 1 && (
                              <span className="map-separator">•</span>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      // Fallback: Show generic map progression for BO3
                      [1, 2, 3].map((mapNum, idx) => {
                        const isCurrentMap = mapNum === match.map_number;
                        let mapName = `Map ${mapNum}`;
                        if (mapNum === match.map_number) {
                          const cleanName = match.map_name.replace('de_', '');
                          mapName = cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
                        }

                        return (
                          <React.Fragment key={mapNum}>
                            <span
                              className={`map-item ${isCurrentMap ? 'current' : ''}`}
                              title={mapName}
                            >
                              {mapName}
                            </span>
                            {idx < 2 && <span className="map-separator">•</span>}
                          </React.Fragment>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <span className="match-time">{match.map_name}</span>
                )}
              </div>
            </Link>
          );
        })}

        {remainingCount > 0 && (
          <div className="more-live-matches">
            <div className="more-live-matches__content">
              <span className="more-count">+{remainingCount}</span>
              <span className="more-text">more live</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveMatchBar;
